import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import { firebaseAuthErrorMessage, useAuth } from "@/hooks/useAuth";
import {
  trackAuthEmailSigninAttempt,
  trackAuthEmailSigninError,
  trackAuthEmailSigninSuccess,
  trackAuthEmailSignupAttempt,
  trackAuthEmailSignupError,
  trackAuthEmailSignupSuccess,
  trackAuthEmailVerificationSent,
  trackAuthFormModeToggle,
  trackAuthSigninClick,
  trackAuthSigninError,
  trackAuthSigninSuccess,
} from "@/lib/analytics";
import { fontStack } from "@/theme";

type Mode = "signin" | "signup";

interface AuthFormProps {
  /** Where this form is surfaced — drives GA `source` param. */
  source: string;
  /** Initial mode. Defaults to sign-in. */
  initialMode?: Mode;
  /** Fires after a successful sign-in OR sign-up. */
  onSuccess?: () => void;
  /** Fires when the user taps "Forgot password?". */
  onForgotPassword?: (email: string) => void;
}

/**
 * Shared email-password + Google sign-in/sign-up form. Used from every
 * auth surface — AuthPromptDialog, AuthGate, Welcome — so every entry
 * point offers the same options and the same error vocabulary.
 *
 * The "forgot password" handler is a callback so each host can decide
 * how to render the reset flow (a second dialog, a second page, etc.)
 * rather than this component trying to know about modal stacks.
 */
export default function AuthForm({
  source,
  initialMode = "signin",
  onSuccess,
  onForgotPassword,
}: AuthFormProps) {
  const { signInWithGoogle, signInWithEmail, createAccountWithEmail } = useAuth();

  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const primaryLabel = mode === "signin" ? "Sign in" : "Create account";
  const toggleLabel =
    mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in";

  const handleToggle = () => {
    const next = mode === "signin" ? "signup" : "signin";
    setMode(next);
    setError(null);
    trackAuthFormModeToggle(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    const trimmed = email.trim();
    if (!trimmed || !password) {
      setError("Enter an email and password.");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "signup") {
        trackAuthEmailSignupAttempt(source);
        await createAccountWithEmail(trimmed, password);
        trackAuthEmailSignupSuccess(source);
        // createAccountWithEmail also fires the verification email.
        trackAuthEmailVerificationSent(source);
      } else {
        trackAuthEmailSigninAttempt(source);
        await signInWithEmail(trimmed, password);
        trackAuthEmailSigninSuccess(source);
      }
      onSuccess?.();
    } catch (err) {
      const msg = firebaseAuthErrorMessage(err);
      setError(msg || "Something went wrong. Try again.");
      const code = String((err as { code?: unknown })?.code ?? "unknown");
      if (mode === "signup") {
        trackAuthEmailSignupError(source, code);
      } else {
        trackAuthEmailSigninError(source, code);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    trackAuthSigninClick(source);
    try {
      await signInWithGoogle();
      trackAuthSigninSuccess();
      onSuccess?.();
    } catch (err) {
      const msg = firebaseAuthErrorMessage(err);
      if (msg) setError(msg);
      trackAuthSigninError((err as Error)?.message ?? "unknown");
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
    >
      <TextField
        type="email"
        label="Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete={mode === "signup" ? "email" : "username"}
        disabled={submitting}
        fullWidth
        size="small"
        required
      />
      <TextField
        type="password"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete={mode === "signup" ? "new-password" : "current-password"}
        disabled={submitting}
        helperText={mode === "signup" ? "At least 6 characters" : undefined}
        fullWidth
        size="small"
        required
      />

      {error && (
        <Alert severity="error" sx={{ fontSize: "0.85rem", py: 0.5 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        sx={{
          fontFamily: fontStack.sans,
          textTransform: "none",
          fontWeight: 500,
          py: 1,
        }}
      >
        {submitting ? "…" : primaryLabel}
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 0.5,
        }}
      >
        <Link
          component="button"
          type="button"
          onClick={handleToggle}
          underline="hover"
          disabled={submitting}
          sx={{
            fontFamily: fontStack.sans,
            fontSize: "0.8rem",
            color: "ink.muted",
            "&:hover": { color: "ink.main" },
          }}
        >
          {toggleLabel}
        </Link>
        {mode === "signin" && onForgotPassword && (
          <Link
            component="button"
            type="button"
            onClick={() => onForgotPassword(email.trim())}
            underline="hover"
            disabled={submitting}
            sx={{
              fontFamily: fontStack.sans,
              fontSize: "0.8rem",
              color: "ink.muted",
              "&:hover": { color: "ink.main" },
            }}
          >
            Forgot password?
          </Link>
        )}
      </Box>

      <Divider sx={{ my: 1 }}>
        <Typography
          sx={{
            fontFamily: fontStack.mono,
            fontSize: "0.7rem",
            color: "ink.muted",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            px: 1,
          }}
        >
          or
        </Typography>
      </Divider>

      <Button
        type="button"
        variant="outlined"
        onClick={handleGoogle}
        disabled={submitting}
        startIcon={<GoogleIcon />}
        sx={{
          fontFamily: fontStack.sans,
          textTransform: "none",
          fontWeight: 500,
          py: 1,
        }}
      >
        Continue with Google
      </Button>
    </Box>
  );
}
